import { useMemo, useEffect, useState, useCallback, ReactNode } from "react";
import {
  usePlaidLink,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkStableEvent
} from "react-plaid-link";

export { type PlaidLinkOnSuccessMetadata } from "react-plaid-link";

export interface IdentityFlowProps {
  token: string;
  onSuccess: (data: PlaidLinkOnSuccessMetadata) => void;
  onError: (error: string) => void;
  onSkipped: (reason: "lead_idv_already_submitted") => void;
  onExit: () => void;
  env: "sandbox" | "prod" | string;
  LoadingView: ReactNode;
}

export const IdentityFlow = ({
  token,
  onSuccess,
  onError,
  onSkipped,
  onExit,
  env,
  LoadingView
}: IdentityFlowProps) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [leadIdentifier, setLeadIdentifier] = useState<string | null>(null);

  const hostUrl = useMemo(() => {
    // eslint-disable-next-line sonarjs/no-small-switch -- this will be removed
    switch (env) {
      case "prod":
        return "https://api.production.boompay.app";
      case "sandbox":
        return "https://api.sandbox.boompay.app";
      case "qa":
        return "https://api.staging.boompay.app";
      default:
        return "http://localhost:17001";
    }
  }, [env]);

  const handlePlaidSuccess = useCallback(
    async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      try {
        // Step 3: Finish identity verification
        const finishResponse = await fetch(
          `${hostUrl}/screen/leads/${leadIdentifier}/identity_verifications/finish`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              plaid_id: metadata.link_session_id || publicToken
            })
          }
        );

        if (!finishResponse.ok) {
          onError("unable to finish identity verification");
          return;
        }

        // Only call partner onSuccess after successful finish
        onSuccess(metadata);
        // eslint-disable-next-line sonarjs/no-ignored-exceptions
      } catch (error) {
        onError("unable to finish identity verification");
      }
    },
    [onSuccess, onError, hostUrl, leadIdentifier]
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit,
    onEvent: (eventName, metadata) => {
      switch (eventName) {
        case PlaidLinkStableEvent.ERROR:
          onError(metadata.error_message || "Plaid error occurred");
          break;
        case PlaidLinkStableEvent.IDENTITY_VERIFICATION_FAIL_SESSION:
        case PlaidLinkStableEvent.IDENTITY_VERIFICATION_PASS_SESSION:
          // TO DO: Handle identity verification events if needed
          break;
        case PlaidLinkStableEvent.EXIT:
          onExit();
          break;
        default:
          break;
      }
    }
  });

  useEffect(() => {
    if (!token) {
      onError("no lead token");
      return;
    }

    const controller = new AbortController();

    const authenticateAndCreateToken = async () => {
      setIsLoading(true);

      try {
        // Step 1: Authenticate with token
        const authResponse = await fetch(
          `${hostUrl}/screen/auth/jwt/auth_with_token`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({ token }),
            signal: controller.signal
          }
        );

        if (!authResponse.ok) {
          onError("unauthorized");
          return;
        }

        const res = (await authResponse.json()) as {
          customer: { lead_identifier: string };
          access_token: string;
          refresh_token: string;
          token_type: string;
          expires_in: string;
        };

        const leadId = res.customer.lead_identifier;

        const bearerToken = res.access_token;

        // Step 2: Create link token
        const linkTokenResponse = await fetch(
          `${hostUrl}/screen/leads/${leadId}/identity_verifications`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${bearerToken}`
            },
            body: JSON.stringify({
              // TO DO: we must register all partner urls.
              redirect_uri: `${hostUrl}/v1/plaid/oauth_redirect_native`
            }),
            signal: controller.signal
          }
        );

        if (!linkTokenResponse.ok) {
          // Handle 418 status code - IDV already passed
          if (linkTokenResponse.status === 418) {
            onSkipped("lead_idv_already_submitted");
            return;
          }
          onError("unable to create identity token");
          return;
        }

        const linkTokenData = await linkTokenResponse.json();

        setLeadIdentifier(leadId);
        setLinkToken(linkTokenData.link_token);
        setIsLoading(false);
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError")
          // Request was aborted, don't call onError
          return;

        onError("unable to create identity token");
        setIsLoading(false);
      }
    };

    authenticateAndCreateToken();

    return () => controller.abort();
  }, [token, hostUrl, onError]);

  useEffect(() => {
    if (ready && linkToken) open();
  }, [ready, linkToken, open]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (isLoading) return <>{LoadingView}</>;

  return null;
};
